import { auth } from '../../config/firebase';
import { showAlert } from '../../utils/alert';
import { cleanUndefinedDeep } from '../../utils/cleanUndefined';
import { errorReporting } from '../../services/errorReportingService';

export type FirebaseProfile = {
  dailyRecommendations?: any;
  currentRecommendations?: any;
  profileSummary?: any;
  coreProfile?: any;
  onboardingAnswers?: any;
  activeContext?: any;
  chatHistory?: any[];
  [key: string]: any;
};

export const fetchOldProfileData = async (
  previousUserId: string,
): Promise<FirebaseProfile | null> => {
  try {
    const { db, doc, getDoc } = require('../../config/firebase');
    const oldProfileRef = doc(db, 'privateProfile', previousUserId);
    const oldProfileSnap = await getDoc(oldProfileRef);
    if (!oldProfileSnap.exists()) return null;
    return oldProfileSnap.data();
  } catch (error) {
    console.error('Failed to fetch old profile data before migration:', error);
    return null;
  }
};

export const migrateAnonymousProfile = async (
  previousUserId: string,
  newUserId: string,
  oldProfileData: FirebaseProfile | null,
) => {
  // Only use data we already captured while anonymous; avoid post-login reads/deletes
  if (!oldProfileData) return;

  try {
    const { db, doc, getDoc, setDoc } = require('../../config/firebase');
    const newProfileRef = doc(db, 'privateProfile', newUserId);
    const newProfileSnap = await getDoc(newProfileRef);

    if (newProfileSnap.exists()) {
      const newProfileData = newProfileSnap.data();
      const merged: FirebaseProfile = cleanUndefinedDeep({
        ...newProfileData,
        dailyRecommendations: newProfileData.dailyRecommendations || oldProfileData.dailyRecommendations,
        currentRecommendations: newProfileData.currentRecommendations || oldProfileData.currentRecommendations,
        profileSummary: newProfileData.profileSummary || oldProfileData.profileSummary,
        coreProfile: newProfileData.coreProfile || oldProfileData.coreProfile,
        onboardingAnswers: newProfileData.onboardingAnswers || oldProfileData.onboardingAnswers,
        activeContext: newProfileData.activeContext || oldProfileData.activeContext,
        chatHistory: [...(newProfileData.chatHistory || []), ...(oldProfileData.chatHistory || [])],
        updatedAt: new Date().toISOString(),
      });
      await setDoc(newProfileRef, merged);
    } else {
      await setDoc(
        newProfileRef,
        cleanUndefinedDeep({
          ...oldProfileData,
          userId: newUserId,
          updatedAt: new Date().toISOString(),
        }),
      );
    }

    // Clear cache so dashboard fetches fresh data
    const { PrivateProfileService } = require('../../services/privateProfileService');
    await PrivateProfileService.invalidateProfileCache(newUserId);
  } catch (migrationError) {
    // Best-effort: log and continue, but do not block login
    console.warn('⚠️ Anonymous profile migration skipped', {
      code: (migrationError as any)?.code,
      message: (migrationError as any)?.message,
      previousUserId,
      newUserId,
    });
  }
};

export const hasRecommendationsForUser = async (userId: string): Promise<boolean> => {
  try {
    const { PrivateProfileService } = require('../../services/privateProfileService');
    const profileResult = await PrivateProfileService.getProfile(userId);
    if (!profileResult.success || !profileResult.data) return false;
    const data = profileResult.data as FirebaseProfile;
    return !!(data.dailyRecommendations || data.currentRecommendations);
  } catch {
    return false;
  }
};

type SocialSignInOptions = {
  signIn: () => Promise<{ success: boolean; data?: any; error?: string }>;
  providerLabel: 'Apple' | 'Google';
  loading: boolean;
  setLoading: (value: boolean) => void;
  onNavigateAfterAuth: (hasRecommendations: boolean, userId?: string | null) => Promise<void>;
};

export const handleSocialSignIn = async ({
  signIn,
  providerLabel,
  loading,
  setLoading,
  onNavigateAfterAuth,
}: SocialSignInOptions) => {
  if (loading) {
    return;
  }

  // Declare these outside try block so they're available in catch block for error reporting
  let previousUserId: string | undefined;
  let wasAnonymous: boolean | undefined;

  try {
    setLoading(true);
    
    // Capture anonymous user ID and data before sign-in
    previousUserId = auth.currentUser?.uid;
    wasAnonymous = auth.currentUser?.isAnonymous;
    const oldProfileData =
      previousUserId && wasAnonymous ? await fetchOldProfileData(previousUserId) : null;
    
    const result = await signIn();
    
    // If redirect was initiated, stop processing as the page will reload/redirect
    if (result.success && result.data?.pendingRedirect) {
      return;
    }
    
    if (!result.success) {
      // Handle popup blocker with detailed user-friendly message
      if (result.error === 'POPUP_BLOCKED' && result.data?.userMessage) {
        showAlert('Pop-ups Blocked', result.data.userMessage);
        return;
      }
      
      // Handle other errors (except user cancellation)
      if (result.error && result.error !== `User canceled ${providerLabel} Sign-In.`) {
        showAlert(`${providerLabel} Sign-In Failed`, result.error);
      }
      return;
    }
    
    const userId = result.data?.user?.uid || auth.currentUser?.uid || null;
    
    // Handle migration differently for web vs native
    const { Platform } = require('react-native');
    
    if (previousUserId && wasAnonymous && userId && previousUserId !== userId) {
      if (Platform.OS === 'web') {
        // Web: Store migration data in sessionStorage, then reload
        if (oldProfileData && typeof sessionStorage !== 'undefined') {
          try {
            sessionStorage.setItem('mentorist_pending_migration', JSON.stringify({
              previousUserId,
              newUserId: userId,
              oldProfileData,
              timestamp: Date.now(),
            }));
            console.log('💾 Stored migration data for post-reload processing');
          } catch (err) {
            console.warn('⚠️ Failed to store migration data:', err);
          }
        }
        
        // Reload page for clean state
        console.log('✅ OAuth complete, reloading page');
        window.location.reload();
        return; // Don't proceed after reload is triggered
      } else {
        // Native: Migrate immediately (no reload needed)
        await migrateAnonymousProfile(previousUserId, userId, oldProfileData);
        
        // Check profile and navigate
        const hasRecommendations = await hasRecommendationsForUser(userId);
        await onNavigateAfterAuth(hasRecommendations, userId);
      }
    } else {
      // No migration needed - just navigate
      if (Platform.OS === 'web') {
        console.log('✅ OAuth complete, reloading page');
        window.location.reload();
      } else {
        const hasRecommendations = userId ? await hasRecommendationsForUser(userId) : false;
        await onNavigateAfterAuth(hasRecommendations, userId);
      }
    }
  } catch (error: any) {
    // Report to Sentry - breadcrumbs from authService already captured what matters
    errorReporting.reportError(error, {
      userId: auth.currentUser?.uid,
      screen: 'AuthShared',
      action: 'handleSocialSignIn',
      metadata: {
        provider: providerLabel,
        errorCode: error?.code,
        wasMigration: previousUserId && wasAnonymous && previousUserId !== auth.currentUser?.uid,
      },
    });
    
    showAlert(
      `${providerLabel} Sign-In Failed`,
      error?.message || `An unknown error occurred during ${providerLabel} Sign-In.`
    );
  } finally {
    setLoading(false);
  }
};

